import { popinApi, dashApi } from './api';
import Logger from '../util/Logger';
import { formatResponseDate } from '../features/ai-insights/api/utils';

// Configuration for OpenAI API
const OPENAI_API_ENDPOINT = process.env.REACT_APP_OPENAI_API_ENDPOINT || 'https://api.openai.com/v1/chat/completions';
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

/**
 * Simplified AI API service that follows the same pattern as the Results tab
 * This version only handles basic event data fetching without complex authentication
 * or external API dependencies
 */
const AIApiV1 = {
  /**
   * Get event details by ID
   * @param {string} eventId - ID of the event to fetch
   * @returns {Promise<object>} - Promise resolving to event details
   */
  getEventDetails: (eventId) => {
    console.log('========== AIApiV1.getEventDetails CALLED ==========');
    console.log('Input eventId:', eventId);
    
    return Promise.resolve().then(() => {
      if (!eventId) {
        const error = new Error('Must pass an eventId as a param');
        console.error('Invalid eventId (null/undefined):', error);
        Logger.info('reject getEventDetails()');
        throw error;
      }
      
      // Extract event ID from URL if needed
      let cleanEventId = eventId;
      if (eventId.includes('/')) {
        console.log('EventId contains path separators, attempting to extract clean ID');
        try {
          // Handle full URLs (with domain)
          if (eventId.includes('://')) {
            console.log('EventId appears to be a full URL');
            const url = new URL(eventId);
            const pathParts = url.pathname.split('/').filter(part => part.length > 0);
            console.log('URL path parts:', pathParts);
            
            // Look for the event ID in the path (typically after 'q' or 'r')
            for (let i = 0; i < pathParts.length; i++) {
              if ((pathParts[i] === 'q' || pathParts[i] === 'r') && i + 1 < pathParts.length) {
                cleanEventId = pathParts[i + 1];
                console.log('Extracted event ID from URL:', { original: eventId, extracted: cleanEventId });
                break;
              }
            }
          } else {
            // Handle path-only format
            console.log('EventId appears to be a path');
            const parts = eventId.split('/').filter(part => part.length > 0);
            console.log('Path parts:', parts);
            
            // Look for ID after 'q' or 'r'
            for (let i = 0; i < parts.length; i++) {
              if ((parts[i] === 'q' || parts[i] === 'r') && i + 1 < parts.length) {
                cleanEventId = parts[i + 1];
                console.log('Extracted event ID from path:', { original: eventId, extracted: cleanEventId });
                break;
              }
            }
          }
        } catch (error) {
          console.error('Error extracting event ID:', error);
          console.error('Stack trace:', error.stack);
          // Continue with original ID if extraction fails
        }
      }
      
      const url = `/events/detail/${cleanEventId}`;
      console.log('Making API request to:', url);
      
      return popinApi.get(url)
        .then(response => {
          console.log('Event details API response:', JSON.stringify(response, null, 2));
          
          // Log important fields for debugging
          console.log('Event details key fields:', {
            id: response.id,
            name: response.name,
            title: response.title,
            question_type: response.question_type,
            phase: response.phase,
            summary: response.summary
          });
          
          return response;
        })
        .catch(error => {
          console.error('Error fetching event details:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          console.error('For event ID:', cleanEventId);
          return { eventDetailsError: error };
        });
    });
  },
  
  /**
   * Get all posts for an event
   * @param {string} eventId - ID of the event
   * @returns {Promise<object>} - Promise resolving to posts data
   */
  getEventPosts: (eventId) => {
    console.log('========== AIApiV1.getEventPosts CALLED ==========');
    console.log('Input eventId:', eventId);
    
    return Promise.resolve().then(() => {
      if (!eventId) {
        const error = new Error('Must pass an eventId as a param');
        console.error('Invalid eventId (null/undefined):', error);
        Logger.info('reject getEventPosts()');
        throw error;
      }
      
      const url = `/events/${eventId}/posts?include_children=true`;
      console.log('Making API request to:', url);
      
      return popinApi.get(url)
        .then(response => {
          console.log('Event posts API response summary:');
          console.log('- Total posts:', response.posts?.length || 0);
          
          // Check if posts exist and have expected structure
          if (!response.posts) {
            console.warn('No posts array found in response:', Object.keys(response));
          } else if (response.posts.length === 0) {
            console.log('Posts array is empty');
          } else {
            // Log post structure for the first few posts
            console.log('- First 3 posts:', JSON.stringify((response.posts || []).slice(0, 3), null, 2));
            
            // Log post field structure for debugging
            console.log('- Post fields structure:', Object.keys(response.posts[0]));
            
            // Check for children (comments)
            const hasComments = response.posts.some(post => post.children && post.children.length > 0);
            console.log('- Posts have comments:', hasComments);
            
            if (hasComments) {
              // Log example comment structure
              const postWithComments = response.posts.find(post => post.children && post.children.length > 0);
              if (postWithComments) {
                console.log('- Example comment structure:', JSON.stringify(postWithComments.children[0], null, 2));
              }
            }
          }
          
          return response;
        })
        .catch(error => {
          console.error('Error fetching event posts:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          console.error('For event ID:', eventId);
          return { postsError: error };
        });
    });
  },
  
  /**
   * Get event statistics
   * @param {string} eventId - ID of the event
   * @returns {Promise<object>} - Promise resolving to event statistics
   */
  getEventStats: (eventId) => {
    console.log('========== AIApiV1.getEventStats CALLED ==========');
    console.log('Input eventId:', eventId);
    
    return Promise.resolve().then(() => {
      if (!eventId) {
        const error = new Error('Must pass an eventId as a param');
        console.error('Invalid eventId (null/undefined):', error);
        Logger.info('reject getEventStats()');
        throw error;
      }
      
      const url = `/events/${eventId}/stats`;
      console.log('Making API request to dashApi:', url);
      
      return dashApi.get(url)
        .then(response => {
          console.log('Event stats API response:', JSON.stringify(response, null, 2));
          return response;
        })
        .catch(error => {
          console.error('Error fetching event stats:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          console.error('For event ID:', eventId);
          return { statsError: error };
        });
    });
  },
  
  // Use standardized formatResponseDate helper from utils.js
  formatResponseDate,
  
  /**
   * Send a prompt to OpenAI and get a response
   * @param {string} prompt - The prompt to send to OpenAI
   * @param {string} model - The OpenAI model to use (default: gpt-4)
   * @returns {Promise<object>} - Promise resolving to OpenAI response
   */
  getAIInsights: (prompt, model = 'gpt-4') => {
    console.log('========== AIApiV1.getAIInsights CALLED ==========');
    console.log('Using model:', model);
    console.log('Prompt length:', prompt.length);
    
    return Promise.resolve().then(() => {
      if (!prompt) {
        const error = new Error('Must provide a prompt');
        console.error('Invalid prompt (null/undefined):', error);
        Logger.info('reject getAIInsights()');
        throw error;
      }
      
      // Check if OpenAI API key is configured
      if (!OPENAI_API_KEY) {
        console.warn('OpenAI API key is not configured. Using mock response.');
        // Return a mock response for development without API key
        return {
          id: 'mock-response-id',
          object: 'chat.completion',
          created: Math.floor(Date.now() / 1000),
          model: model,
          choices: [{
            index: 0,
            message: {
              role: 'assistant',
              content: 'This is a mock response because the OpenAI API key is not configured.\n\nPlease set the REACT_APP_OPENAI_API_KEY environment variable to use the actual OpenAI API.'
            },
            finish_reason: 'stop'
          }],
          usage: {
            prompt_tokens: prompt.length / 4,
            completion_tokens: 50,
            total_tokens: prompt.length / 4 + 50
          }
        };
      }
      
      // Prepare request to OpenAI API
      const requestData = {
        model: model,
        messages: [
          { 
            role: 'system', 
            content: `You are an expert survey data analyst and insights specialist. Format your response in markdown with the following sections:

1. Start with an executive summary (1-2 paragraphs)
2. ## Key Themes
   - List 3-5 key themes as bullet points
3. ## Actionable Insights
   - List 3-5 actionable insights as bullet points
4. ## Next Steps
   - List 3-5 recommended next steps as bullet points

Ensure each section has the exact heading format shown above.`
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7,
        max_tokens: 2000,
        top_p: 1,
        frequency_penalty: 0,
        presence_penalty: 0
      };
      
      console.log('Making API request to OpenAI:', OPENAI_API_ENDPOINT);
      console.log('Request data:', JSON.stringify(requestData, null, 2));
      
      // Make request to OpenAI API
      return fetch(OPENAI_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        },
        body: JSON.stringify(requestData)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('OpenAI API response:', JSON.stringify(data, null, 2));
        return data;
      })
      .catch(error => {
        console.error('Error fetching AI insights:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        return { aiInsightsError: error };
      });
    });
  }
};

export default AIApiV1;
